import { useState } from 'react';
import { Box, Button, Card, Flex, Heading, Text, TextField } from '@radix-ui/themes';
import styled from '@emotion/styled';
import { login, getLoginDetails } from '@/api/endpoints';
import { setSession, type Session } from '@/api/session';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f8f9fb;
`;

export function DevLogin({ onLogin }: { onLogin: (session: Session) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isPending, setIsPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsPending(true);

    try {
      const loginRes = await login({ username, password, AppId: 2 });
      if (!loginRes.Result || !loginRes.Data?.SessionGId) {
        throw new Error(loginRes.Message || 'Login response missing SessionGId.');
      }

      const detailsRes = await getLoginDetails({ SessionId: loginRes.Data.SessionGId });
      if (!detailsRes.Result || !detailsRes.Data?.LspId) {
        throw new Error(detailsRes.Message || 'Login details missing LspId.');
      }

      const session: Session = {
        SessionId: loginRes.Data.SessionGId,
        UserId: loginRes.Data.Id,
        LocId: detailsRes.Data.LocId,
        LspId: detailsRes.Data.LspId,
        Email: detailsRes.Data.Email || detailsRes.Data.UserName || loginRes.Data.UserName || null,
      };

      setSession(session);
      onLogin(session);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error. Is the API proxy running?');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Wrapper>
      <Card size="4" style={{ width: 400 }}>
        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Heading size="5">Dev Login</Heading>
            <Text size="2" color="gray">
              Sign in with your LeafLogix credentials
            </Text>

            <label>
              <Text size="2" weight="medium" mb="1" asChild>
                <span>Username</span>
              </Text>
              <TextField.Root
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                required
              />
            </label>

            <label>
              <Text size="2" weight="medium" mb="1" asChild>
                <span>Password</span>
              </Text>
              <TextField.Root
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                required
              />
            </label>

            {error && (
              <Text size="2" color="red">
                {error}
              </Text>
            )}

            <Box>
              <Button type="submit" disabled={isPending} style={{ width: '100%' }}>
                {isPending ? 'Signing in...' : 'Sign in'}
              </Button>
            </Box>
          </Flex>
        </form>
      </Card>
    </Wrapper>
  );
}
